import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { signalStore, withComputed, withProps, withState } from '@ngrx/signals';
import { Events, on, withEventHandlers } from '@ngrx/signals/events';
import { mapResponse } from '@ngrx/operators';
import { withTrackedReducer } from '@angular-architects/ngrx-toolkit';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY } from 'rxjs';
import {
  auditTime,
  filter,
  map,
  mergeMap,
  switchMap,
  tap,
} from 'rxjs/operators';
import {
  ColumnSizes,
  NotificationService,
  Pagination,
  PAGINATION_DEFAULT_PAGE_SIZE,
  Sorting,
  StateMemento,
} from 'wflow-cdk';
import { PanelFilterState } from 'wflow-ui';
import { FilterCondition } from 'wflow-variables';
import { withStoreDevtools } from 'wflow-main-app/src/environments';
import { PeppolListScope } from '../peppol-items.interface';
import { PeppolAction } from '../../../interfaces/peppol-action.interface';
import { PEPPOL_ACTION_API_PATHS } from '../../../const/peppol-message-actions.const';
import { peppolStatusEvents } from '../../../store/peppol.events';
import { PeppolItem } from '../../../interfaces/peppol-item.interface';
import { PEPPOL_COLUMN_SIZES } from '../../../tokens/peppol-column-sizes.token';
import {
  PEPPOL_MESSAGES_API,
  PeppolMessagesApi,
} from '../../../tokens/peppol-messages-api.token';
import {
  createPeppolFilterConditionsFromState,
  createPeppolScopeConditions,
} from '../../../utils/create-filter-conditions.util';
import {
  peppolItemsApiEvents,
  peppolItemsPageEvents,
} from './peppol-items.events';

interface PeppolItemsState {
  storageKeyPrefix: string;
  scope: PeppolListScope | null;
  search: string;
  sort: Sorting;
  pagination: Pagination;
  filters: PanelFilterState;
  columnSizes: ColumnSizes | null;
  loading: boolean;
  loadError: HttpErrorResponse | null;
  items: PeppolItem[];
  totalItems: number;
}

const initialState: PeppolItemsState = {
  storageKeyPrefix: '',
  scope: null,
  search: '',
  sort: { active: '', direction: 'desc' },
  pagination: { pageNo: 0, pageSize: PAGINATION_DEFAULT_PAGE_SIZE },
  filters: {},
  columnSizes: null,
  loading: true,
  loadError: null,
  items: [],
  totalItems: 0,
};

export const PeppolItemsStore = signalStore(
  withStoreDevtools('peppol-items'),
  withState<PeppolItemsState>(initialState),
  withProps(() => ({
    storage: {
      pageSize: null as StateMemento<number> | null,
      filters: null as StateMemento<PanelFilterState> | null,
    },
  })),
  withComputed((store) => ({
    filterConditions: computed(() =>
      buildFilterConditions(store.scope(), store.filters()),
    ),
  })),
  withTrackedReducer(
    on(peppolItemsPageEvents.initialized, ({ payload }) => ({
      storageKeyPrefix: payload.storageKeyPrefix,
      scope: payload.scope,
    })),
    on(peppolItemsApiEvents.storageHydrated, ({ payload }) => ({
      pagination: payload.pagination,
      filters: payload.filters,
    })),
    on(peppolItemsApiEvents.columnSizesLoaded, ({ payload }, state) => {
      if (state.columnSizes) {
        return {};
      }

      return { columnSizes: payload };
    }),
    on(peppolItemsPageEvents.searchChanged, ({ payload }, state) => ({
      search: payload,
      pagination: { ...state.pagination, pageNo: 0 },
    })),
    on(peppolItemsPageEvents.filtersChanged, ({ payload }, state) => ({
      filters: { ...payload },
      pagination: { ...state.pagination, pageNo: 0 },
    })),
    on(peppolItemsPageEvents.paginationChanged, ({ payload }) => ({
      pagination: payload,
    })),
    on(peppolItemsPageEvents.sortChanged, ({ payload }) => ({
      sort: payload,
    })),
    on(peppolItemsPageEvents.columnSizesChanged, ({ payload }) => ({
      columnSizes: payload,
    })),
    on(peppolItemsApiEvents.itemsLoadStarted, () => ({
      loading: true,
      loadError: null,
    })),
    on(peppolItemsApiEvents.itemsLoaded, ({ payload: list }) => ({
      items: list?.items ?? [],
      totalItems: list?.totalItems ?? 0,
      pagination: { pageNo: list.page, pageSize: list.pageSize },
      loading: false,
      loadError: null,
    })),
    on(peppolItemsApiEvents.itemsLoadFailed, ({ payload }) => ({
      loading: false,
      loadError: payload.error,
    })),
  ),
  withEventHandlers((store) => {
    const events = inject(Events);
    const peppolMessagesApi = inject(PEPPOL_MESSAGES_API);
    const notification = inject(NotificationService);
    const translate = inject(TranslateService);
    const columnSizesSvc = inject(PEPPOL_COLUMN_SIZES);

    return {
      hydrateStorage$: events.on(peppolItemsPageEvents.initialized).pipe(
        map(({ payload: { storageKeyPrefix } }) => {
          store.storage.pageSize = new StateMemento<number>({
            key: `${storageKeyPrefix}_PAGE_SIZE`,
            storage: localStorage,
          });
          store.storage.filters = new StateMemento<PanelFilterState>({
            key: `${storageKeyPrefix}_FILTERS`,
            storage: sessionStorage,
          });

          return peppolItemsApiEvents.storageHydrated({
            pagination: {
              pageNo: 0,
              pageSize:
                store.storage.pageSize.getState() ??
                PAGINATION_DEFAULT_PAGE_SIZE,
            },
            filters: store.storage.filters.getState() ?? {},
          });
        }),
      ),

      hydrateColumnSizes$: events.on(peppolItemsPageEvents.initialized).pipe(
        switchMap(() => columnSizesSvc.get$()),
        map((sizes) => peppolItemsApiEvents.columnSizesLoaded(sizes ?? null)),
      ),

      triggerLoad$: events
        .on(
          peppolItemsApiEvents.storageHydrated,
          peppolItemsPageEvents.searchChanged,
          peppolItemsPageEvents.filtersChanged,
          peppolItemsPageEvents.paginationChanged,
          peppolItemsPageEvents.sortChanged,
          peppolStatusEvents.changed,
          peppolItemsApiEvents.actionSucceeded,
        )
        .pipe(
          auditTime(300),
          filter(() => !!store.storageKeyPrefix()),
          map(() => peppolItemsApiEvents.itemsLoadStarted()),
        ),

      fetchItems$: events.on(peppolItemsApiEvents.itemsLoadStarted).pipe(
        switchMap(() => {
          const { pageNo, pageSize } = store.pagination();
          return peppolMessagesApi
            .getItems({
              search: store.search(),
              sort: buildSort(store.sort()),
              page: pageNo,
              pageSize,
              filters: store.filterConditions(),
            })
            .pipe(
              mapResponse({
                next: (list) => peppolItemsApiEvents.itemsLoaded(list),
                error: (error: HttpErrorResponse) =>
                  peppolItemsApiEvents.itemsLoadFailed({ error }),
              }),
            );
        }),
      ),

      notifyLoadFailure$: events.on(peppolItemsApiEvents.itemsLoadFailed).pipe(
        tap(({ payload }) =>
          notification.error({
            text:
              payload.error?.error?.message ??
              payload.error?.message ??
              translate.instant('BasicErrorHandlerService_DefaultErrorTitle'),
          }),
        ),
      ),

      persistFilters$: events
        .on(peppolItemsPageEvents.filtersChanged)
        .pipe(tap(({ payload }) => store.storage.filters?.change(payload))),

      persistPageSize$: events
        .on(peppolItemsPageEvents.paginationChanged)
        .pipe(
          tap(({ payload }) =>
            store.storage.pageSize?.change(payload.pageSize),
          ),
        ),

      persistColumnSizes$: events
        .on(peppolItemsPageEvents.columnSizesChanged)
        .pipe(tap(({ payload }) => columnSizesSvc.set(payload))),

      // mergeMap (not switchMap): concurrent row actions must complete independently.
      runAction$: events.on(peppolItemsPageEvents.actionInvoked).pipe(
        mergeMap(({ payload: { action, item } }) => {
          const request$ = buildActionRequest(
            action as PeppolAction,
            item,
            peppolMessagesApi,
          );
          if (!request$) return EMPTY;

          return request$.pipe(
            mapResponse({
              next: () => peppolItemsApiEvents.actionSucceeded({ action }),
              error: (error: HttpErrorResponse) =>
                peppolItemsApiEvents.actionFailed({ action, error }),
            }),
          );
        }),
      ),

      runReject$: events.on(peppolItemsPageEvents.rejectConfirmed).pipe(
        mergeMap(({ payload: { item, request } }) =>
          peppolMessagesApi.rejectItem(item.id, request).pipe(
            mapResponse({
              next: () =>
                peppolItemsApiEvents.actionSucceeded({
                  action: PeppolAction.Reject,
                }),
              error: (error: HttpErrorResponse) =>
                peppolItemsApiEvents.actionFailed({
                  action: PeppolAction.Reject,
                  error,
                }),
            }),
          ),
        ),
      ),

      runCreateDocument$: events
        .on(peppolItemsPageEvents.createDocumentConfirmed)
        .pipe(
          mergeMap(({ payload: { item, typeId } }) =>
            peppolMessagesApi.createDocumentFromItem(item.id, typeId).pipe(
              mapResponse({
                next: () =>
                  peppolItemsApiEvents.actionSucceeded({
                    action: PeppolAction.CreateDocument,
                  }),
                error: (error: HttpErrorResponse) =>
                  peppolItemsApiEvents.actionFailed({
                    action: PeppolAction.CreateDocument,
                    error,
                  }),
              }),
            ),
          ),
        ),

      notifyActionSuccess$: events
        .on(peppolItemsApiEvents.actionSucceeded)
        .pipe(
          tap(({ payload }) =>
            notification.success({
              title: translate.instant(`PEPPOL.ACTIONS.${payload.action}`),
              text: translate.instant(
                `PEPPOL.ACTION_RESULTS.${payload.action}`,
              ),
            }),
          ),
        ),

      notifyActionFailure$: events.on(peppolItemsApiEvents.actionFailed).pipe(
        tap(({ payload }) =>
          notification.error({
            text:
              payload.error?.error?.description ??
              translate.instant('PEPPOL.ACTION_FAILED'),
            title: translate.instant(`PEPPOL.ACTIONS.${payload.action}`),
          }),
        ),
      ),
    };
  }),
);

function buildFilterConditions(
  scope: PeppolListScope | null,
  filters: PanelFilterState,
): FilterCondition[] {
  return [
    ...(scope ? createPeppolScopeConditions(scope) : []),
    ...(filters ? createPeppolFilterConditionsFromState(filters) : []),
  ];
}

function buildSort(value: Sorting): string {
  if (!value?.active) return '';
  return `${value.active} ${value.direction ?? 'desc'}`;
}

function buildActionRequest(
  action: PeppolAction,
  item: PeppolItem,
  peppolMessagesApi: PeppolMessagesApi,
) {
  const apiPath = PEPPOL_ACTION_API_PATHS[action];
  return apiPath ? peppolMessagesApi.sendItemAction(item, apiPath) : null;
}
